import { Box, Dialog, Divider, Grid, IconButton, Menu, MenuItem, SelectChangeEvent, Skeleton, Theme, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ReportsRightLayout } from '../../InvestorReport/index';
import { maskInfo, } from '../../../utils/utilityFunctions';
import { TextDatePicker } from '../../onboarding/DatePickerWithTextFeild';
import { MFSubmitButton, MFTextField } from '../../../lib/formik';
import FormMultiSelect from '../../../lib/formik/FormMultiSelect';
import { useEffect, useRef, useState } from 'react';
import { ReportsListObj, ReportsListRes } from '../../../redux-store/types/api-types';
import dayjs from "dayjs";
import MFSelectField from '../../../lib/formik/SelectField';
import { RootStateType } from '../../../redux-store/reducers';
import { clientStatementDownload, createClientStatement, getClientStatementList, getReportsAnalystInvestorList, getReportsInvestorList, updateClientStatementStatus } from '../../../redux-store/actions/reports';
import * as Yup from 'yup';
import { reportDownload } from '../../Reports/report-utils';
import { DialogCloseIcon, InfoIconOutlined, RefreshIcon, YellowWarning } from '../../../assets/SvgIcons';
import { CONTENT_TYPE, REPORTS_FILTER_TYPE_OPTIONS, REQUESTED_REPORTS, USER_ROLES } from '../../../utils/constant';
import FormikSearchDropdown from '../../../lib/formik/FormikSearchDropdown';
import { Order, PaginationObj, SortingObj } from '../../investorOverview/types';
import ClientStatementTable from '../../ClientStatementTable';
import { ClientStatementReportsList, CreateClientStatementResponseBody, GetClientStatementListResponseBody, UpdateClientStatementStatusResponseBody } from '../../../redux-store/types/onBoarding';
import MFCheckbox from '../../../lib/formik/Checkbox';

interface Values {
    selectedInvestor: ReportsListObj | null;
    filterType: string;
    isHeldAway: boolean;
    filterTypeOptions: Array<{ key: string; value: string; }>;
    familyName: string;
    endDate: any;
    reportSections: Array<string>
}

const INTERVAL_DURATION = 60 * 1000

export function ClientStatement({
    handleSideSectionDisabled
}: {
    handleSideSectionDisabled: (value: boolean) => void
}): JSX.Element {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(false);
    const { iwappRmId, isCustFamilyHead = "" } = useSelector((state: RootStateType) => state.id);
    const { role = '' } = useSelector((state: RootStateType) => state.auth);

    const [investorListData, setInvestorListData] = useState<ReportsListRes>({} as ReportsListRes);
    const [isInitiating, setIsInitiating] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)
    const [isTableDataLoading, setTableDataLoading] = useState<boolean>(false)
    const [tableData, setTableData] = useState<Array<ClientStatementReportsList>>([])
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: "createdAt"
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [selectedJobIdForDownload, setSelectedJobIdForDownload] = useState<string | null>(null)
    const [selectedJobIdForShare, setSelectedJobIdForShare] = useState<string | null>(null)
    const [shareMenuAnchorEl, setShareMenuAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({})
    const [isSharing, setIsSharing] = useState<boolean>(false)

    const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);

    const formikRef = useRef<FormikProps<Values>>(null);

    const tablePaginationObjRef = useRef(tablePaginationObj)
    const tableSortingObjRef = useRef(tableSortingObj)
    const tableDataRef = useRef(tableData)

    const fetchInvestorList = async () => {
        try {
            setLoading(true);

            let response = {} as ReportsListRes

            if ([USER_ROLES.ANALYST].includes(role)) {
                response = await (dispatch(getReportsAnalystInvestorList({
                    analystId: `${iwappRmId}`
                })) as unknown as Promise<ReportsListRes>);
            } else {
                response = await (dispatch(getReportsInvestorList({
                    rmId: `${iwappRmId}`,
                    reportName: "client-statement"
                })) as unknown as Promise<ReportsListRes>);
            }

            setInvestorListData(response);
            if (!response?.isSuccess) {
                setNoInvestorDialogVisible(true)
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoading(false);
        }
    };

    const fetchTableData = async ({
        currentPage,
        rowsPerPage,
        sort,
        order,
        updatePendingReports
    }: {
        currentPage: number;
        rowsPerPage: number;
        sort: string;
        order: Order;
        updatePendingReports: boolean;
    }) => {
        try {
            setTableDataLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                sort,
                order,
                currentPage,
                rowsPerPage,
                updatePendingReports
            }

            const {
                count = 0,
                reports = []
            } = await (dispatch(updateClientStatementStatus(payload)) as unknown as Promise<UpdateClientStatementStatusResponseBody>)

            setTableData(reports)
            setTableSortingObj({
                order,
                orderBy: sort
            })
            setTablePaginationObj({
                currentPage: currentPage - 1,
                rowsPerPage,
                totalRows: count
            })
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setTableDataLoading(false)
        }
    }

    useEffect(() => {
        tablePaginationObjRef.current = tablePaginationObj;
        tableSortingObjRef.current = tableSortingObj;
        tableDataRef.current = tableData
    }, [tablePaginationObj, tableSortingObj, tableData]);

    useEffect(() => {
        if (!iwappRmId) return

        const fetchData = async () => {
            await Promise.allSettled([
                fetchInvestorList(),
                fetchTableData({
                    currentPage: tablePaginationObj.currentPage + 1,
                    rowsPerPage: tablePaginationObj.rowsPerPage,
                    sort: tableSortingObj.orderBy,
                    order: tableSortingObj.order,
                    updatePendingReports: true
                })
            ])
        }

        fetchData()

        const intervalId = setInterval(async () => {
            if ((tableDataRef.current ?? []).some(obj => `${obj?.status ?? ""}`.toLowerCase() === "pending")) {

                await fetchTableData({
                    currentPage: tablePaginationObjRef.current.currentPage + 1,
                    rowsPerPage: tablePaginationObjRef.current.rowsPerPage,
                    sort: tableSortingObjRef.current.orderBy,
                    order: tableSortingObjRef.current.order,
                    updatePendingReports: true,
                });

            }
        }, INTERVAL_DURATION);

        return () => clearInterval(intervalId);

    }, [iwappRmId])

    const todayDate = new Date();
    const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").subtract(1, "day").format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");

    const initialValues: Values = {
        selectedInvestor: null,
        filterType: "PORTFOLIO",
        isHeldAway: false,
        filterTypeOptions: REPORTS_FILTER_TYPE_OPTIONS,
        familyName: "",
        endDate: formattedBusinessDate,
        reportSections: Object.keys(REQUESTED_REPORTS).filter(key => key !== "4")
    };


    const mfTransactionValidations = Yup.object().shape({
        selectedInvestor: Yup
            .object()
            .nullable()
            .test(
                "investor-required",
                "Please select an investor",
                function (value) { return !!value }
            ),
        filterType: Yup
            .string()
            .required('Please select a Filter type'),
        endDate: Yup
            .date()
            .required('As on Date is required')
            .typeError('As on Date is required')
            .max(formattedBusinessDate, "As on Date should not be greater than system's date"),
        // reportSections: Yup
        //     .array()
        //     .of(Yup.string())
        //     .min(1, 'Please select a report section')
    })

    const handleRefresh = async () => {
        await fetchTableData({
            currentPage: 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            sort: tableSortingObj.orderBy,
            order: tableSortingObj.order,
            updatePendingReports: true
        })
    }

    const handleSubmit = async ({
        selectedInvestor,
        filterType,
        isHeldAway,
        endDate,
        reportSections
    }: Values) => {
        try {
            setIsInitiating(true)
            handleSideSectionDisabled(true)

            const idMap: { [key: string]: string } = {
                FAMILY: selectedInvestor?.familyId ?? "",
                CUSTOMER: selectedInvestor?.panNo ?? "",
                PORTFOLIO: selectedInvestor?.portfolioList?.[0]?.portfolioId ?? ""
            }

            const payload = {
                clientCode: `${selectedInvestor?.clientCode ?? ""}`,
                rmId: `${iwappRmId}`,
                endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
                accountType: selectedInvestor?.accountType ?? "",
                requested_reports: (reportSections ?? []).map(Number),
                consolidatedLevel: filterType === "CUSTOMER" ? "PAN" : filterType,
                custFilterValue: idMap[filterType ?? ""] ?? "",
                heldAway: isHeldAway ? 'Y' : 'N'
            }

            const response = await (dispatch(createClientStatement(payload)) as unknown as Promise<CreateClientStatementResponseBody>)

            if (response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'success',
                    autoHideDuration: 5000,
                });

                await handleRefresh()

                return
            }
            if (!response?.isSuccess) {
                enqueueSnackbar(`${response?.message ?? ""}`, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });

                return
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setIsInitiating(false)
            handleSideSectionDisabled(false)
        }
    }

    const handleNoInvestorDialogClose = () => {
        setNoInvestorDialogVisible(false)
    }

    const handleInvestorChange = (selectedObj: Record<string, any>) => {
        if (!formikRef.current) return

        const { values, setValues } = formikRef.current ?? {}

        if (!selectedObj) {
            setValues({
                ...values,
                selectedInvestor: null,
                filterType: "PORTFOLIO",
                isHeldAway: false,
                filterTypeOptions: REPORTS_FILTER_TYPE_OPTIONS,
                familyName: "",
                reportSections: Object.keys(REQUESTED_REPORTS).filter(key => key !== "4")
            })

            return
        }

        setValues({
            ...values,
            selectedInvestor: selectedObj as ReportsListObj,
            filterType: "PORTFOLIO",
            isHeldAway: false,
            filterTypeOptions: (isCustFamilyHead === "Y" || selectedObj?.isFamilyHead === "Y") ? REPORTS_FILTER_TYPE_OPTIONS : REPORTS_FILTER_TYPE_OPTIONS.filter(item => `${item?.key}`.toLowerCase() !== "family"),
            familyName: "",
            reportSections: (values?.reportSections ?? [])
                .filter(key => key !== "4")
        })
    }

    const handleTableSort = async (orderBy: string, order: Order) => {
        await fetchTableData({
            currentPage: 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            sort: orderBy,
            order,
            updatePendingReports: false
        })
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        await fetchTableData({
            currentPage: 1,
            rowsPerPage: Number(event.target.value),
            sort: tableSortingObj.orderBy,
            order: tableSortingObj.order,
            updatePendingReports: false
        })
    }

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        await fetchTableData({
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage,
            sort: tableSortingObj.orderBy,
            order: tableSortingObj.order,
            updatePendingReports: false
        })
    }

    const handleReportDownload = async (jobId: string, clientName: string, shareVia: 'download' | 'email' | 'whatsapp' = 'download') => {
        try {
            if (shareVia === 'download') {
                setIsDownloading(true)
                setSelectedJobIdForDownload(jobId)
            } else {
                setIsSharing(true)
                setSelectedJobIdForShare(jobId)
            }
            handleSideSectionDisabled(true)

            const payload = { jobId, shareVia }

            const response = await (dispatch(clientStatementDownload(payload)) as unknown as Promise<any>)

            if (shareVia === 'download') {
                if (response.type === CONTENT_TYPE.PDF) {
                    enqueueSnackbar('File downloaded successfully.', {
                        variant: 'success',
                        autoHideDuration: 5000,
                    });
                    reportDownload(response as unknown as Blob, `${clientName ?? ""}-ClientStatement.pdf`);
                } else {
                    enqueueSnackbar('File download failed!.', {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });
                }
            } else {
                if (response?.isSuccess) {
                    enqueueSnackbar(`File shared via ${shareVia === 'email' ? 'Email' : 'WhatsApp'} successfully.`, {
                        variant: 'success',
                        autoHideDuration: 5000,
                    });
                } else {
                    enqueueSnackbar(`File share via ${shareVia === 'email' ? 'Email' : 'WhatsApp'} failed!.`, {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });
                }
            }

        } catch (e) {
            console.log("Error:", e)
            enqueueSnackbar(`Operation failed!.`, {
                variant: 'error',
                autoHideDuration: 5000,
            });
        } finally {
            if (shareVia === 'download') {
                setSelectedJobIdForDownload(null)
            } else {
                setSelectedJobIdForShare(null)
            }
            handleSideSectionDisabled(false)
        }
    }

    const handleShareMenuOpen = (event: React.MouseEvent<HTMLElement>, jobId: string) => {
        setShareMenuAnchorEl({ ...shareMenuAnchorEl, [jobId]: event.currentTarget })
    }

    const handleShareMenuClose = (jobId: string) => {
        setShareMenuAnchorEl({ ...shareMenuAnchorEl, [jobId]: null })
    }

    const handleShareVia = async (jobId: string, clientName: string, shareVia: 'email' | 'whatsapp') => {
        handleShareMenuClose(jobId)
        await handleReportDownload(jobId, clientName, shareVia)
    }

    const handleFilterTypeChange = ({ target: { value } }: SelectChangeEvent<unknown>) => {
        if (!formikRef?.current) {
            return
        }

        const { values: formikValues, setValues } = formikRef.current

        const modifiedReportSections = [...(formikValues?.reportSections ?? [])]

        if (`${value ?? ""}`.toLowerCase() === "family") {
            modifiedReportSections.push("4")
        }

        setValues({
            ...formikValues,
            filterType: `${value ?? ""}`,
            isHeldAway: false,
            ...((`${value ?? ""}`.toLowerCase() === "family") ? {
                familyName: formikValues?.selectedInvestor?.familyName ?? ""
            } : {
                familyName: ""
            }),
            reportSections: (modifiedReportSections)
                .filter(key => {
                    if (`${value ?? ""}`.toLowerCase() !== "family") {
                        return key !== "4"
                    }
                    return true
                })
        })
    }

    return (
        <>
            <ReportsRightLayout >
                <Typography
                    variant='h2'
                    sx={{
                        color: 'text.primary',
                        lineHeight: '25px',
                        letterSpacing: '0.01px',
                        mb: { xs: '20px', sm: '40px' },
                    }}>
                    Client Statement
                </Typography>

                <Formik
                    innerRef={formikRef}
                    validationSchema={mfTransactionValidations}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ values }) => (
                        <Form>
                            <Grid container rowSpacing={2} >
                                <Grid item container columnSpacing={3} rowSpacing={2}>
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <FormikSearchDropdown
                                                name='selectedInvestor'
                                                label='Select Investor *'
                                                options={(investorListData?.result ?? [])?.map((obj: ReportsListObj) => ({
                                                    ...obj,
                                                    label: `${maskInfo(obj.customerName)} (${obj?.clientCode ?? ""}) ${obj?.accountType ? " - " + obj.accountType : ""}`,
                                                    value: obj.clientCode
                                                }))}
                                                onChange={handleInvestorChange}
                                                disabled={loading || isDownloading || isNavbarInfoLoading || isInitiating}
                                            />
                                        )}
                                    </Grid>
                                    {/* Filter Type Dropdown start */}
                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <MFSelectField
                                                name='filterType'
                                                label="Filter Type *"
                                                items={values?.filterTypeOptions ?? []}
                                                disabled={
                                                    loading ||
                                                    isDownloading ||
                                                    isNavbarInfoLoading ||
                                                    isInitiating ||
                                                    !values?.selectedInvestor
                                                }
                                                onChange={handleFilterTypeChange}
                                            />
                                        )}
                                    </Grid>
                                    {/* Filter Type Dropdown end */}

                                    {/* Family Name start */}
                                    {(`${values?.filterType ?? ""}`.toLowerCase() === "family") && (
                                        <Grid item xs={12} lg={6}>
                                            <MFTextField
                                                name="familyName"
                                                label="Family Name"
                                                placeholder=""
                                                disabled={true}
                                                value={values?.familyName ?? ""}
                                            />
                                        </Grid>
                                    )}
                                    {/* Family Name end */}

                                    <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <>
                                                <TextDatePicker
                                                    name="endDate"
                                                    label="As on date *"
                                                    placeholder=""
                                                    maxDate={new Date(formattedBusinessDate)}
                                                    disabled={
                                                        loading ||
                                                        isDownloading ||
                                                        isNavbarInfoLoading ||
                                                        isInitiating
                                                    }
                                                />
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        columnGap: "10px",
                                                        marginTop: { xs: "3px", sm: "-10px" }
                                                    }}>
                                                    <InfoIconOutlined />
                                                    <Typography
                                                        variant='subtitle_600'
                                                        sx={(theme: Theme) => ({
                                                            color: theme.palette.grey[500]
                                                        })}
                                                    >
                                                        Reports are generated Since Inception.
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Grid>
                                    {/* <Grid item xs={12} lg={6}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <FormMultiSelect
                                                name="reportSections"
                                                label='Select Report Sections *'
                                                options={Object.keys(REQUESTED_REPORTS)
                                                    .filter(key => {
                                                        if ((values?.filterType ?? "").toLowerCase() !== "family") {
                                                            return key !== "4"
                                                        }
                                                        return true
                                                    })
                                                    .map(key => ({
                                                        label: REQUESTED_REPORTS[key],
                                                        value: key
                                                    }))}
                                                sx={{
                                                    "&.MuiOutlinedInput-root": {
                                                        "& fieldset>legend": {
                                                            fontSize: { xs: "0.6em", md: "0.7em", lg: "0.8em" }
                                                        }
                                                    }
                                                }}
                                                disabled={
                                                    loading ||
                                                    isDownloading ||
                                                    isNavbarInfoLoading ||
                                                    isInitiating
                                                }
                                            />
                                        )}
                                    </Grid> */}
                                    {/* HeldAway Checkbox start */}
                                    {(`${values?.filterType ?? ""}`.toLowerCase() !== "portfolio") && (
                                        <Grid
                                            item
                                            container
                                            xs={12}
                                            lg={6}
                                            sx={{ alignItems: "center" }}
                                        >
                                            <Grid item>
                                                <MFCheckbox
                                                    name="isHeldAway"
                                                    label="Include Held Away"
                                                    sx={(theme: Theme) => ({
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        color: `${theme.palette.text.primary}`,
                                                        "& :disabled": {
                                                            color: `${theme.palette.text.secondary}`,
                                                        }
                                                    })}
                                                    disabled={loading || isDownloading || isNavbarInfoLoading || isInitiating}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}
                                    {/* HeldAway Checkbox end */}
                                </Grid>
                                <Grid item container columnSpacing={3} justifyContent={"flex-end"}>
                                    <Grid item xs={6} sm={8} />
                                    <Grid item xs={6} sm={4}>
                                        {(loading || isNavbarInfoLoading) && <Skeleton
                                            animation={'wave'}
                                            height={90}

                                        />}
                                        {(!loading && !isNavbarInfoLoading) && (
                                            <MFSubmitButton
                                                label="Initiate"
                                                loadingEnable={isInitiating}
                                                disabled={loading || isDownloading}
                                                fullWidth
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid >
                        </Form >
                    )}
                </Formik >
                <Divider sx={{ mt: 4, mb: 4 }} />
                <Box sx={{ marginTop: 1 }}>
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                columnGap: "10px"
                            }}
                        >
                            <Box>
                                <Typography
                                    variant='body_600'
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    Existing Client Statements
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        columnGap: "5px",
                                        marginLeft: '-2px',
                                        mt: 0.5
                                    }}>
                                    <InfoIconOutlined />
                                    <Typography
                                        variant='subtitle_600'
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.grey[500]
                                        })}
                                    >
                                        Statements will be available for 1 day only.
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton aria-label="Refresh data"
                                sx={{
                                    cursor: 'pointer',
                                    "&: disabled": {
                                        opacity: 0.5
                                    }
                                }}
                                onClick={handleRefresh}
                                disabled={isTableDataLoading}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ marginTop: 5 }}>
                            <ClientStatementTable
                                isLoading={isTableDataLoading}
                                tableData={tableData}
                                handleTableSort={handleTableSort}
                                tablePaginationObj={tablePaginationObj}
                                handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                                handleTablePageChange={handleTablePageChange}
                                tableSortingObj={tableSortingObj}
                                handleReportDownload={handleReportDownload}
                                isDownloading={isDownloading}
                                selectedJobIdForDownload={selectedJobIdForDownload}
                                handleShareMenuOpen={handleShareMenuOpen}
                                shareMenuAnchorEl={shareMenuAnchorEl}
                                handleShareMenuClose={handleShareMenuClose}
                                handleShareVia={handleShareVia}
                                isSharing={isSharing}
                                selectedJobIdForShare={selectedJobIdForShare}
                            />
                        </Box>
                    </Box>
                </Box>
            </ReportsRightLayout >
            <Dialog
                onClose={handleNoInvestorDialogClose}
                open={isNoInvestorDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "12px",
                                alignItems: "center"
                            }}
                        >
                            <YellowWarning />
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                No Investor Found!!
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleNoInvestorDialogClose}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};


